import { useCallback, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router";
import { Box, Container, makeStyles } from "@material-ui/core";
import { colors, Loader } from "swyft-react-core";
import { useSelector, useDispatch } from "react-redux";
import { RootState, history } from "../../app/rootReducer";
import {
  fetchQuote,
  removeAddonQuote,
  changeQuantityAddonQuote,
  addSubscriptionQuote,
  removeSubscriptionQuote,
  syncQuote,
  releaseQuote,
  viewedCheckout
} from "../../store/QuoteSlice";
import { PageState } from "../../api/Types";
import { QuoteLineItem } from "../../api/Quote/QuoteLineItem";
import { useCookies } from "react-cookie";
import { CookieQuoteId } from "../../app/consts";
import QuoteCartTop from "./components/QuoteCartTop";
import QuoteCartContent from "./components/QuoteCartContent";
import { AddonForm } from "../../api/Addon/AddonApi";
import { toast } from "react-toastify";
import QuoteRemoveDialog from "../quotePage/components/QuoteRemoveDialog";
import { fetchProductSubscriptions } from "../../store/ProductSubscriptionSlice";
import { ProductSubscription } from "../../api/Product/ProductSubscriptionApi";
import { mapProductSubscriptionToForm, QuoteSubscriptionForm } from "../../api/QuoteSubscription/QuoteSubscriptionApi";
import { hasAddons, hasSubscriptions, quoteAvailableSubscriptions } from "../../app/utils";
import { EEventType, eventAnalytic } from "../../app/analytics";
import { ReviewContainer } from "../quotePage/ReviewContainer";
import { useGetEventSlot, useScheduleQuote } from "../../api/EventSlot/EventSlotApi";
import QuoteCartSchedulPopupDialog from "./components/QuoteCartSchedulPopupDialog";
import { applyPromoCode, removePromoCode } from "../../api/Quote/QuoteApi";
import { QuoteAvailableDeduction } from "./components/QuoteAvailableDeduction";
import { useQuoteSelectedEvent } from "./components/UseQuoteSelectedEvent";
import OverrideTheme from "../../components/OverrideTheme";

type Props = RouteComponentProps<{ id: string }>;

const useStyles = makeStyles(() => ({
  alternate: {
    background: colors.alternate
  }
}));

const QuoteCartPage = ({ match }: Props) => {
  eventAnalytic.useEffect(EEventType.PageCartView);
  const classes = useStyles();
  const dispatch = useDispatch();
  const [openRemove, setOpenRemove] = useState(false);
  const [openSchedule, setOpenSchedule] = useState(false);
  const [expired, setExpired] = useState(false);
  const [addonForRemove, setAddonForRemove] = useState<QuoteLineItem | null>(null);
  const [cookies, setCookie] = useCookies();

  const { pageState, error, quote } = useSelector((state: RootState) => state.quote);
  const [subscriptionForRemove, setSubscriptionForRemove] = useState<ProductSubscription | null>(null);
  const { data: selectedEventSlot, isLoading: isEventLoading } = useGetEventSlot(quote?.eventSlotId);
  const { mutate: scheduleQuote } = useScheduleQuote();
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);

  const quoteId = match.params.id;

  useEffect(() => {
    dispatch(fetchProductSubscriptions());
    dispatch(syncQuote(quoteId, true));
    dispatch(viewedCheckout(quoteId));
  }, [quoteId, dispatch]);

  useQuoteSelectedEvent(
    quote,
    useCallback(() => {
      dispatch(releaseQuote(quoteId));
      setExpired(true);
      setOpenSchedule(true);
    }, [dispatch, quoteId, setExpired, setOpenSchedule]),
    useCallback(() => {
      setOpenSchedule(true);
    }, [setOpenSchedule])
  );

  if (!quote || !subscriptions || isEventLoading) {
    return <Loader />;
  }

  if (pageState === PageState.Error || error) {
    return <div>Error</div>;
  }

  if (cookies[CookieQuoteId] !== quote.id) {
    setCookie(CookieQuoteId, quote.id, { path: "/" });
  }

  const availableSubscriptions = quoteAvailableSubscriptions(quote, subscriptions);

  const handleBack = (lastStep: boolean) => {
    const backState = {
      lastStep: lastStep
    };
    if (!hasAddons(quote.addons) && !hasSubscriptions(availableSubscriptions) && !quote.eventSlotId) {
      history.push(`/quote/${quote.id}`, backState);
    } else {
      history.push(`/quote/addon/${quote.id}`, backState);
    }
    return;
  };

  const onRemoveAddon = (addon: QuoteLineItem) => {
    setAddonForRemove(addon);
    onRemoveDialogOpen();
  };

  const handleRemove = (addon: QuoteLineItem) => {
    dispatch(
      removeAddonQuote(quoteId, {
        addonId: addon.productId
      } as AddonForm)
    );
    eventAnalytic.record(EEventType.RemovedAddon);
    toast.success(addon.name + " has been removed");
  };

  const onChangeQuantity = (addon: QuoteLineItem, quantity: number) => {
    dispatch(
      changeQuantityAddonQuote(quoteId, {
        addonId: addon.productId,
        quantity: quantity
      } as AddonForm)
    );
  };

  const onRemoveDialogOpen = () => {
    setOpenRemove(true);
  };

  const onRemoveDialogClose = () => {
    setOpenRemove(false);
  };

  const onScheduleDialogClose = () => {
    setOpenSchedule(false);
  };

  const onAddSubscription = (subscription: ProductSubscription) => {
    const selected = quote.subscriptions?.some((i) => i.productId === subscription.id);
    if (selected) {
      onRemoveSubscription(subscription);
      return;
    }
    eventAnalytic.record(subscription);
    dispatch(addSubscriptionQuote(quoteId, mapProductSubscriptionToForm(subscription)));
    toast.success(subscription.name + " has been added");
  };

  const onRemoveSubscription = function (subscription: ProductSubscription) {
    setSubscriptionForRemove(subscription);
    onRemoveDialogOpen();
  };

  const handleRemoveSubscription = function (subscription: ProductSubscription) {
    dispatch(
      removeSubscriptionQuote(quoteId, {
        productId: subscription.id
      } as QuoteSubscriptionForm)
    );
    eventAnalytic.record(EEventType.RemovedSubscription);
    toast.success(subscription.name + " has been removed");
  };

  const onPay = async () => {
    await onLockAgain();
  };

  const onLockAgain = async () => {
    if (selectedEventSlot) {
      await scheduleQuote({
        quoteId: quote.id,
        eventSlotId: selectedEventSlot.id
      });
      dispatch(fetchQuote(quoteId));
    }
  };

  const onApplyPromoCode = async (promoCode: string) => {
    await applyPromoCode(quote.id, promoCode);
    dispatch(fetchQuote(quoteId));
  };

  const onDeletePromoCode = async (quoteDeductionId: string) => {
    await removePromoCode(quote.id, quoteDeductionId);
    dispatch(fetchQuote(quoteId));
  };

  return (
    <Box>
      <QuoteCartSchedulPopupDialog
        open={openSchedule}
        onClose={onScheduleDialogClose}
        quote={quote}
        expired={expired}
        onScheduleBack={() => handleBack(false)}
      />
      <OverrideTheme>
        <QuoteRemoveDialog
          open={openRemove}
          onClose={onRemoveDialogClose}
          onRemove={handleRemove}
          addon={addonForRemove}
          subscription={subscriptionForRemove}
          onRemoveSubscription={handleRemoveSubscription}
        />
        <QuoteCartTop quote={quote} onBack={() => handleBack(true)} />
        <QuoteAvailableDeduction quote={quote} />
      </OverrideTheme>
      <Container maxWidth="lg">
        <QuoteCartContent
          quote={quote}
          onChangeQuantity={onChangeQuantity}
          onRemoveAddon={onRemoveAddon}
          subscriptions={availableSubscriptions}
          onRemoveSubscription={onRemoveSubscription}
          onAddSubscription={onAddSubscription}
          selectedEventSlot={selectedEventSlot}
          onPay={onPay}
          onApplyPromoCode={onApplyPromoCode}
          onDeletePromoCode={onDeletePromoCode}
        />
      </Container>

      <Box className={classes.alternate} mb={2} py={4} textAlign="center">
        <OverrideTheme>
          <ReviewContainer type={quote.type} />
        </OverrideTheme>
      </Box>
    </Box>
  );
};

export default QuoteCartPage;
